import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { NativeSelect } from '../../components/ui/native-select';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { 
  Users, Plus, Search, RefreshCcw, Edit, Trash2, 
  UserPlus, UserMinus, GraduationCap, BookOpen, 
  ChevronRight, CheckCircle, XCircle, Filter, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const GroupsManagementPage = ({ onMenuClick }) => {
  const { lastMessage } = useWebSocket();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCourse, setFilterCourse] = useState('all');
  const [filterTrainer, setFilterTrainer] = useState('all');
  
  // Data for selects
  const [courses, setCourses] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showStudentsDialog, setShowStudentsDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    course_id: '',
    trainer_id: '',
    max_students: 30
  });
  
  // Student assignment
  const [availableStudents, setAvailableStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');

  const fetchGroups = useCallback(async () => {
    try {
      const response = await api.get('/groups');
      setGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error('Erreur lors du chargement des groupes');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCourses = useCallback(async () => {
    try {
      const response = await api.get('/courses');
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  }, []);

  const fetchTrainers = useCallback(async () => {
    try {
      const response = await api.get('/trainers');
      setTrainers(response.data);
    } catch (error) {
      console.error('Error fetching trainers:', error);
    }
  }, []);

  const fetchAllStudents = useCallback(async () => {
    try {
      const response = await api.get('/students');
      setAllStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
    fetchCourses();
    fetchTrainers();
    fetchAllStudents();
  }, [fetchGroups, fetchCourses, fetchTrainers, fetchAllStudents]);

  useEffect(() => {
    if (lastMessage?.type === 'data_refresh' && lastMessage?.event_type?.includes('group')) {
      fetchGroups();
    }
  }, [lastMessage, fetchGroups]);

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Le nom du groupe est requis');
      return;
    }
    if (!formData.course_id) {
      toast.error('Veuillez sélectionner un cours associé.');
      return;
    }
    if (!formData.trainer_id) {
      toast.error('Veuillez assigner un formateur avant de créer la classe.');
      return;
    }

    try {
      await api.post('/groups', {
        name: formData.name,
        description: formData.description,
        course_id: formData.course_id ? parseInt(formData.course_id) : null,
        trainer_id: formData.trainer_id ? parseInt(formData.trainer_id) : null,
        max_students: formData.max_students
      });
      toast.success('Groupe créé avec succès');
      setShowCreateDialog(false);
      resetForm();
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Le nom du groupe est requis');
      return;
    }
    if (!formData.course_id) {
      toast.error('Veuillez sélectionner un cours associé.');
      return;
    }
    if (!formData.trainer_id) {
      toast.error('Veuillez assigner un formateur.');
      return;
    }

    try {
      await api.put(`/groups/${selectedGroup.id}`, {
        name: formData.name,
        description: formData.description,
        course_id: formData.course_id ? parseInt(formData.course_id) : null,
        trainer_id: formData.trainer_id ? parseInt(formData.trainer_id) : null,
        max_students: formData.max_students
      });
      toast.success('Groupe mis à jour');
      setShowEditDialog(false);
      resetForm();
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (groupId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce groupe ?')) return;

    try {
      await api.delete(`/groups/${groupId}`);
      toast.success('Groupe supprimé');
      fetchGroups();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEdit = (group) => {
    setSelectedGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      course_id: group.course_id?.toString() || '',
      trainer_id: group.trainer_id?.toString() || '',
      max_students: group.max_students
    });
    setShowEditDialog(true);
  };

  const openStudentsDialog = async (group) => {
    setSelectedGroup(group);
    try {
      // Get current students in group
      const detailResponse = await api.get(`/groups/${group.id}`);
      const currentStudentIds = detailResponse.data.students?.map(s => s.id) || [];
      setSelectedStudentIds(currentStudentIds);
      
      // Get available students
      const availableResponse = await api.get(`/groups/${group.id}/available-students`);
      setAvailableStudents(availableResponse.data);
      
      setShowStudentsDialog(true);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const openDetailDialog = async (group) => {
    try {
      const response = await api.get(`/groups/${group.id}`);
      setSelectedGroup(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const handleSaveStudents = async () => {
    try {
      await api.put(`/groups/${selectedGroup.id}/students`, {
        student_ids: selectedStudentIds
      });
      toast.success('Étudiants mis à jour');
      setShowStudentsDialog(false);
      fetchGroups();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const toggleStudent = (studentId) => {
    setSelectedStudentIds(prev => 
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleRemoveStudent = async (studentId) => {
    try {
      await api.delete(`/groups/${selectedGroup.id}/students/${studentId}`);
      toast.success('Étudiant retiré du groupe');
      // Refresh detail
      const response = await api.get(`/groups/${selectedGroup.id}`);
      setSelectedGroup(response.data);
      fetchGroups();
    } catch (error) {
      toast.error('Erreur lors du retrait');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      course_id: '',
      trainer_id: '',
      max_students: 30
    });
    setSelectedGroup(null);
  };

  // Filter groups
  const filteredGroups = groups.filter(group => {
    const matchesSearch = group.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCourse = filterCourse === 'all' || group.course_id?.toString() === filterCourse;
    const matchesTrainer = filterTrainer === 'all' || group.trainer_id?.toString() === filterTrainer;
    return matchesSearch && matchesCourse && matchesTrainer;
  });

  // Filter available students for assignment dialog
  const filteredAvailableStudents = availableStudents.filter(student =>
    student.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
    student.email.toLowerCase().includes(studentSearch.toLowerCase())
  );

  // Stats
  const stats = {
    total: groups.length,
    active: groups.filter(g => g.is_active).length,
    totalStudents: groups.reduce((sum, g) => sum + g.student_count, 0),
    avgSize: groups.length ? Math.round(groups.reduce((sum, g) => sum + g.student_count, 0) / groups.length) : 0
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Gestion des Classes / Groupes" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-violet-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Total groupes</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Users className="w-8 h-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Groupes actifs</p>
                  <p className="text-2xl font-bold">{stats.active}</p>
                </div>
                <CheckCircle className="w-8 h-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Total étudiants</p>
                  <p className="text-2xl font-bold">{stats.totalStudents}</p>
                </div>
                <GraduationCap className="w-8 h-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Taille moyenne</p>
                  <p className="text-2xl font-bold">{stats.avgSize}</p>
                </div>
                <Users className="w-8 h-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher un groupe..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-48"
                data-testid="search-groups"
              />
            </div>
            
            <NativeSelect
              value={filterCourse}
              onChange={setFilterCourse}
              options={[
                { value: 'all', label: 'Tous les cours' },
                ...courses.map(course => ({ value: course.id.toString(), label: course.name }))
              ]}
              placeholder="Tous les cours"
              className="w-40"
              data-testid="filter-course"
            />
            
            <NativeSelect
              value={filterTrainer}
              onChange={setFilterTrainer}
              options={[
                { value: 'all', label: 'Tous les formateurs' },
                ...trainers.map(trainer => ({ value: trainer.id.toString(), label: `${trainer.first_name} ${trainer.last_name}` }))
              ]}
              placeholder="Tous les formateurs"
              className="w-40"
              data-testid="filter-trainer"
            />
            
            <Button variant="outline" size="icon" onClick={fetchGroups}>
              <RefreshCcw className="w-4 h-4" />
            </Button>
          </div>
          
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="create-group-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Groupe
          </Button>
        </div>

        {/* Groups Grid */}
        {filteredGroups.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGroups.map(group => (
              <Card 
                key={group.id} 
                className="hover:shadow-lg transition-shadow"
                data-testid={`group-card-${group.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{group.name}</CardTitle>
                      {group.description && (
                        <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                          {group.description}
                        </p>
                      )}
                    </div>
                    <Badge className={group.is_active 
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30' 
                      : 'bg-slate-100 text-slate-700'
                    }>
                      {group.is_active ? 'Actif' : 'Inactif'}
                    </Badge>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className="space-y-3">
                    {/* Course */}
                    <div className="flex items-center gap-2 text-sm">
                      <BookOpen className="w-4 h-4 text-violet-600" />
                      <span className="text-slate-600 dark:text-slate-400">
                        {group.course_name || 'Aucun cours assigné'}
                      </span>
                    </div>
                    
                    {/* Trainer */}
                    <div className="flex items-center gap-2 text-sm">
                      <GraduationCap className="w-4 h-4 text-blue-600" />
                      <span className="text-slate-600 dark:text-slate-400">
                        {group.trainer_name || 'Aucun formateur assigné'}
                      </span>
                    </div>
                    
                    {/* Students count */}
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="w-4 h-4 text-emerald-600" />
                      <span className="text-slate-600 dark:text-slate-400">
                        {group.student_count} / {group.max_students} étudiants
                      </span>
                    </div>
                    
                    {/* Progress bar for capacity */}
                    <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                      <div 
                        className="bg-violet-600 h-2 rounded-full transition-all"
                        style={{ width: `${Math.min((group.student_count / group.max_students) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 mt-4 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => openDetailDialog(group)}
                      data-testid={`view-group-${group.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Détails
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openStudentsDialog(group)}
                      data-testid={`manage-students-${group.id}`}
                    >
                      <UserPlus className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openEdit(group)}
                      data-testid={`edit-group-${group.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(group.id)}
                      data-testid={`delete-group-${group.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600 dark:text-slate-400">
                {searchTerm || filterCourse !== 'all' || filterTrainer !== 'all' 
                  ? 'Aucun groupe trouvé avec ces critères'
                  : 'Aucun groupe créé'
                }
              </p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Create Group Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-600" />
              Nouveau Groupe
            </DialogTitle>
            <DialogDescription>
              Créez un nouveau groupe et assignez-lui un cours et un formateur
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nom du groupe *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Groupe A - Développement Web"
                data-testid="group-name-input"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description du groupe..."
                rows={2}
                data-testid="group-description-input"
              />
            </div>
            
            <div>
              <Label>Cours associé *</Label>
              <NativeSelect
                value={formData.course_id}
                onChange={(v) => setFormData({ ...formData, course_id: v })}
                options={[
                  { value: '', label: 'Aucun cours' },
                  ...courses.map(course => ({ value: course.id.toString(), label: course.name }))
                ]}
                placeholder="Sélectionner un cours"
                data-testid="group-course-select"
              />
            </div>
            
            <div>
              <Label>Formateur assigné *</Label>
              <NativeSelect
                value={formData.trainer_id}
                onChange={(v) => setFormData({ ...formData, trainer_id: v })}
                options={[
                  { value: '', label: 'Aucun formateur' },
                  ...trainers.map(trainer => ({ value: trainer.id.toString(), label: `${trainer.first_name} ${trainer.last_name}` }))
                ]}
                placeholder="Sélectionner un formateur"
                data-testid="group-trainer-select"
              />
            </div>
            
            <div>
              <Label>Capacité maximale</Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={formData.max_students}
                onChange={(e) => setFormData({ ...formData, max_students: parseInt(e.target.value) || 30 })}
                data-testid="group-max-students-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreate}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="submit-create-group"
            >
              Créer le groupe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-violet-600" />
              Modifier le groupe
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nom du groupe *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="edit-group-name"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>
            
            <div>
              <Label>Cours associé *</Label>
              <NativeSelect
                value={formData.course_id}
                onChange={(v) => setFormData({ ...formData, course_id: v })}
                options={[
                  { value: '', label: 'Aucun cours' },
                  ...courses.map(course => ({ value: course.id.toString(), label: course.name }))
                ]}
                placeholder="Sélectionner un cours"
              />
            </div>
            
            <div>
              <Label>Formateur assigné *</Label>
              <NativeSelect
                value={formData.trainer_id}
                onChange={(v) => setFormData({ ...formData, trainer_id: v })}
                options={[
                  { value: '', label: 'Aucun formateur' },
                  ...trainers.map(trainer => ({ value: trainer.id.toString(), label: `${trainer.first_name} ${trainer.last_name}` }))
                ]}
                placeholder="Sélectionner un formateur"
              />
            </div>
            
            <div>
              <Label>Capacité maximale</Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={formData.max_students}
                onChange={(e) => setFormData({ ...formData, max_students: parseInt(e.target.value) || 30 })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); resetForm(); }}>
              Annuler
            </Button>
            <Button 
              onClick={handleUpdate}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="submit-edit-group"
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Students Dialog */}
      <Dialog open={showStudentsDialog} onOpenChange={setShowStudentsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-violet-600" />
              Gérer les étudiants - {selectedGroup?.name}
            </DialogTitle>
            <DialogDescription>
              Sélectionnez les étudiants à ajouter au groupe
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher un étudiant..."
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                className="pl-10"
                data-testid="search-students"
              />
            </div>
            
            <div className="text-sm text-slate-500 mb-2">
              {selectedStudentIds.length} étudiant(s) sélectionné(s)
            </div>
            
            <div className="max-h-[400px] overflow-y-auto space-y-2 border rounded-lg p-2">
              {/* Currently selected (in group) */}
              {selectedGroup?.students?.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-slate-600 mb-2">Dans le groupe :</p>
                  {selectedGroup.students.map(student => (
                    <div 
                      key={`current-${student.id}`}
                      className="flex items-center justify-between p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg mb-1"
                    >
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                        <span>{student.name}</span>
                        <span className="text-xs text-slate-500">{student.email}</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => {
                          setSelectedStudentIds(prev => prev.filter(id => id !== student.id));
                        }}
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Available students */}
              <p className="text-sm font-medium text-slate-600 mb-2">Étudiants disponibles :</p>
              {filteredAvailableStudents.length > 0 ? (
                filteredAvailableStudents.map(student => (
                  <div 
                    key={student.id}
                    className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedStudentIds.includes(student.id) 
                        ? 'bg-violet-50 dark:bg-violet-900/20' 
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                    onClick={() => toggleStudent(student.id)}
                    data-testid={`student-option-${student.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox 
                        checked={selectedStudentIds.includes(student.id)}
                        onCheckedChange={() => toggleStudent(student.id)}
                      />
                      <div>
                        <p className="font-medium">{student.name}</p>
                        <p className="text-xs text-slate-500">{student.email}</p>
                        {student.other_groups?.length > 0 && (
                          <p className="text-xs text-amber-600">
                            Déjà dans : {student.other_groups.join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-slate-500 py-4">
                  {studentSearch ? 'Aucun étudiant trouvé' : 'Tous les étudiants sont déjà dans ce groupe'}
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStudentsDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSaveStudents}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="save-students"
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-600" />
              {selectedGroup?.name}
            </DialogTitle>
            <DialogDescription>
              Détails du groupe et liste des étudiants
            </DialogDescription>
          </DialogHeader>
          
          {selectedGroup && (
            <div className="space-y-6 py-4">
              {/* Info cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm text-slate-500">Cours</p>
                  <p className="font-medium">{selectedGroup.course_name || 'Non assigné'}</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm text-slate-500">Formateur</p>
                  <p className="font-medium">{selectedGroup.trainer_name || 'Non assigné'}</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm text-slate-500">Étudiants</p>
                  <p className="font-medium">{selectedGroup.student_count} / {selectedGroup.max_students}</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm text-slate-500">Créé le</p>
                  <p className="font-medium">
                    {new Date(selectedGroup.created_at).toLocaleDateString('fr-FR', {
                      day: '2-digit', month: 'long', year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
              
              {/* Description */}
              {selectedGroup.description && (
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">Description</p>
                  <p className="text-slate-700 dark:text-slate-300">{selectedGroup.description}</p>
                </div>
              )}
              
              {/* Students list */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold">Étudiants du groupe</p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setShowDetailDialog(false);
                      openStudentsDialog(selectedGroup);
                    }}
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Gérer
                  </Button>
                </div>
                
                {selectedGroup.students?.length > 0 ? (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {selectedGroup.students.map(student => (
                      <div 
                        key={student.id}
                        className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                        data-testid={`student-row-${student.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                            <span className="text-violet-600 font-medium">
                              {student.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{student.name}</p>
                            <p className="text-sm text-slate-500">{student.email}</p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleRemoveStudent(student.id)}
                        >
                          <UserMinus className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Aucun étudiant dans ce groupe</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupsManagementPage;
